import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Send, X, CornerDownRight } from "lucide-react";
import api from "@/lib/api";
import { getInitials, avatarColor } from "@/lib/avatar";
import { useI18n } from "@/context/I18nContext";
import { REACTIONS, emojiFor, topReactionEmojis } from "@/components/social/reactions";

function Avatar({ name, src, size = 32, onClick }) {
  return (
    <button onClick={onClick} className="rounded-2xl grid place-items-center text-[var(--rc-gold)] font-semibold border-2 border-[var(--rc-gold)] overflow-hidden shrink-0"
      style={{ height: size, width: size, fontSize: size * 0.36, backgroundColor: src ? "var(--rc-bg)" : avatarColor(name) }}>
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : getInitials(name)}
    </button>
  );
}

function CommentItem({ c, isReply, replies = [], ctx }) {
  const { t } = useI18n();
  const { onOpenProfile, react, showReactors, setReplyTo, pickerFor, setPickerFor, openReplies, setOpenReplies } = ctx;
  const myMeta = REACTIONS.find((r) => r.type === c.my_reaction);
  const total = c.reactions_total || 0;
  const tops = topReactionEmojis(c.reaction_counts, 3);
  return (
    <div className="flex items-start gap-2" data-testid={`comment-${c.id}`}>
      <Avatar name={c.author_name} src={c.author_avatar} size={isReply ? 26 : 32} onClick={() => onOpenProfile && onOpenProfile(c.author_id)} />
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl bg-[var(--rc-bg)] px-3 py-2 inline-block max-w-full">
          <div className="text-xs font-medium text-[var(--rc-gold)]">{c.author_name}</div>
          <div className="text-sm [overflow-wrap:anywhere]">{c.text}</div>
        </div>
        <div className="flex items-center gap-3 mt-1 pl-1 text-[11px] text-[var(--rc-muted)] relative">
          {pickerFor === c.id && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setPickerFor(null)} />
              <div className="absolute bottom-full left-0 mb-1 z-20 flex items-center gap-1 rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] px-2 py-1 shadow-xl" data-testid={`comment-react-bar-${c.id}`}>
                {REACTIONS.map((r) => <button key={r.type} onClick={() => react(c.id, r.type)} data-testid={`comment-react-${r.type}-${c.id}`} className="text-xl leading-none hover:scale-125 transition-transform">{r.emoji}</button>)}
              </div>
            </>
          )}
          <button onClick={() => react(c.id, c.my_reaction || "like")} data-testid={`comment-like-${c.id}`} className={myMeta ? "text-[var(--rc-gold)] font-semibold" : "hover:text-[var(--rc-text)]"}>
            {myMeta ? <span className="text-sm">{myMeta.emoji}</span> : t("s_like")}
          </button>
          <button onClick={() => setPickerFor((v) => (v === c.id ? null : c.id))} data-testid={`comment-react-more-${c.id}`} className="hover:text-[var(--rc-text)]">😊</button>
          {total > 0 && (
            <button onClick={() => showReactors(c)} data-testid={`comment-reactions-summary-${c.id}`} className="flex items-center gap-0.5 hover:text-[var(--rc-text)]">
              {tops.map((e, i) => <span key={i} className={i > 0 ? "-ml-1" : ""}>{e}</span>)}<span className="ml-0.5">{total}</span>
            </button>
          )}
          {!isReply && <button onClick={() => setReplyTo({ id: c.id, name: c.author_name })} data-testid={`comment-reply-${c.id}`} className="hover:text-[var(--rc-text)]">{t("s_reply")}</button>}
        </div>
        {!isReply && replies.length > 0 && (
          <button onClick={() => setOpenReplies((o) => ({ ...o, [c.id]: !o[c.id] }))} data-testid={`comment-toggle-replies-${c.id}`} className="mt-1 pl-1 text-[11px] text-[var(--rc-gold)] flex items-center gap-1">
            <CornerDownRight size={12} /> {openReplies[c.id] ? t("s_hide_replies") : t("s_view_replies").replace("{n}", replies.length)}
          </button>
        )}
      </div>
    </div>
  );
}

export default function CommentList({ postId, onOpenProfile, onCountChange, stickyComposer }) {
  const { t } = useI18n();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [openReplies, setOpenReplies] = useState({});
  const [pickerFor, setPickerFor] = useState(null);
  const [reactorsFor, setReactorsFor] = useState(null);
  const [reactors, setReactors] = useState(null);

  useEffect(() => {
    (async () => { try { const { data } = await api.get(`/social/posts/${postId}/comments`); setComments(data); } catch {} })();
  }, [postId]);

  const submit = async () => {
    if (!text.trim()) return;
    const parent_id = replyTo?.id || null;
    try {
      const { data } = await api.post(`/social/posts/${postId}/comments`, { text: text.trim(), parent_id });
      setComments((c) => [...c, data]);
      setText("");
      if (parent_id) setOpenReplies((o) => ({ ...o, [parent_id]: true }));
      setReplyTo(null);
      onCountChange && onCountChange(1);
    } catch {}
  };

  const react = async (cid, type) => {
    setPickerFor(null);
    try { const { data } = await api.post(`/social/comments/${cid}/react`, { type }); setComments((cs) => cs.map((c) => (c.id === cid ? { ...c, ...data } : c))); } catch {}
  };

  const showReactors = async (c) => {
    setReactorsFor(c); setReactors(null);
    try { const { data } = await api.get(`/social/comments/${c.id}/reactions`); setReactors(data); } catch { setReactors([]); }
  };

  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesOf = (id) => comments.filter((c) => c.parent_id === id);
  const ctx = { onOpenProfile, react, showReactors, setReplyTo, pickerFor, setPickerFor, openReplies, setOpenReplies };

  return (
    <div className="px-4 pb-4 space-y-3" data-testid={`comments-${postId}`}>
      {topLevel.map((c) => {
        const replies = repliesOf(c.id);
        return (
          <div key={c.id} className="space-y-2">
            <CommentItem c={c} replies={replies} ctx={ctx} />
            {openReplies[c.id] && replies.length > 0 && (
              <div className="ml-8 space-y-2 pl-2 border-l border-[var(--rc-border)]">
                {replies.map((rc) => <CommentItem key={rc.id} c={rc} isReply ctx={ctx} />)}
              </div>
            )}
          </div>
        );
      })}

      <div className={stickyComposer
          ? "sticky bottom-0 -mx-4 px-4 pt-2 pb-2 bg-[var(--rc-bg)] border-t border-[var(--rc-border)] rc-safe-bottom z-10 space-y-2"
          : "space-y-2"} data-testid={`comment-composer-${postId}`}>
        {replyTo && (
          <div className="flex items-center gap-2 text-xs text-[var(--rc-muted)] pl-1" data-testid="reply-context">
            <CornerDownRight size={12} /> {t("s_replying_to")} <span className="text-[var(--rc-gold)]">{replyTo.name}</span>
            <button onClick={() => setReplyTo(null)} className="ml-1"><X size={13} /></button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder={replyTo ? t("s_write_reply") : t("s_write_comment")} data-testid={`comment-input-${postId}`}
            className="flex-1 min-w-0 rounded-full bg-[var(--rc-bg)] border border-[var(--rc-border)] px-4 py-2 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
          <button onClick={submit} data-testid={`comment-send-${postId}`} className="h-9 w-9 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black shrink-0"><Send size={15} /></button>
        </div>
      </div>

      {reactorsFor && createPortal(
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60" onClick={() => setReactorsFor(null)} data-testid={`comment-reactors-modal-${reactorsFor.id}`}>
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-sm max-h-[70vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">{t("s_reactions")} · {reactorsFor.reactions_total || 0}</div>
              <button onClick={() => setReactorsFor(null)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-[var(--rc-bg)]"><X size={18} /></button>
            </div>
            {reactors === null ? <div className="text-center py-8 text-[var(--rc-muted)] text-sm">{t("s_loading")}</div>
              : reactors.length === 0 ? <div className="text-center py-8 text-[var(--rc-muted)] text-sm">—</div>
                : <div className="space-y-2.5">
                    {reactors.map((r) => (
                      <div key={r.user_id} className="flex items-center gap-3" data-testid={`comment-reactor-${reactorsFor.id}-${r.user_id}`}>
                        <div className="relative shrink-0">
                          <Avatar name={r.name} src={r.avatar} size={36} onClick={() => { setReactorsFor(null); onOpenProfile && onOpenProfile(r.user_id); }} />
                          <span className="absolute -bottom-1 -right-1 text-sm leading-none">{emojiFor(r.type)}</span>
                        </div>
                        <span className="flex-1 min-w-0 truncate text-sm font-medium">{r.name}</span>
                      </div>
                    ))}
                  </div>}
          </div>
        </div>, document.body)}
    </div>
  );
}
